import { ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { AppTopBar } from "./AppTopBar";
import { AppSidebar } from "./AppSidebar";
import { type BrandOption } from "./BrandSwitcher";
import { type OrgOption } from "./UserMenu";

const ACTIVE_BRAND_KEY = "reputely.activeBrandId";

export type AppShellContext = {
  user: { id: string | null; email: string; name: string | null };
  orgs: OrgOption[];
  activeOrgId: string;
  brands: BrandOption[];
  activeBrandId: string;
  onBrandChange: (id: string) => void;
  onOrgRenamed: (name: string) => void;
  isOwner: boolean;
};

export function AppShell({
  children,
}: {
  children: (ctx: AppShellContext) => ReactNode;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [meLoading, setMeLoading] = useState(true);
  const [me, setMe] = useState<{
    user: { id: string | null; email: string; name: string | null };
    orgs: OrgOption[];
    activeOrgId: string;
  } | null>(null);
  const [brands, setBrands] = useState<BrandOption[] | null>(null);
  const [activeBrandId, setActiveBrandId] = useState<string>("");
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api<{
      user: { id: string | null; email: string; name: string | null };
      orgs: OrgOption[];
      activeOrgId: string;
    }>("/auth/me").then((res) => {
      if (cancelled) return;
      if (!res.ok || !res.data) {
        navigate("/login", { replace: true });
        return;
      }
      setMe(res.data);
      setMeLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    api<{
      brands: {
        id: string;
        name: string;
        storefrontUrl?: string | null;
        storefrontDiscoveryStatus?: BrandOption["storefrontDiscoveryStatus"];
        brandRegistry: boolean;
        brandRegistryAnsweredAt: string | null;
        collaboratorStatus: BrandOption["collaboratorStatus"];
        inviteSentAt: string | null;
        verifiedAt: string | null;
      }[];
    }>("/customer/brands").then((res) => {
        if (cancelled) return;
        const list: BrandOption[] = (res.data?.brands ?? []).map((b) => ({
          id: b.id,
          name: b.name,
          storefrontUrl: b.storefrontUrl ?? null,
          storefrontDiscoveryStatus: b.storefrontDiscoveryStatus,
          brandRegistry: b.brandRegistry,
          brandRegistryAnsweredAt: b.brandRegistryAnsweredAt,
          collaboratorStatus: b.collaboratorStatus,
          inviteSentAt: b.inviteSentAt,
          verifiedAt: b.verifiedAt,
        }));
        setBrands(list);
        const stored =
          typeof window !== "undefined"
            ? window.localStorage.getItem(ACTIVE_BRAND_KEY)
            : null;
        const next =
          (stored && list.find((b) => b.id === stored)?.id) ?? list[0]?.id ?? "";
        setActiveBrandId(next);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [me]);

  // While any brand is still discovering products from its storefront, poll
  // /customer/brands every 5s so the UI advances out of the "Discovering…"
  // state without a manual refresh.
  useEffect(() => {
    if (!brands) return;
    const anyDiscovering = brands.some(
      (b) => b.storefrontDiscoveryStatus === "discovering"
    );
    if (!anyDiscovering) return;
    const id = setInterval(() => {
      api<{
        brands: {
          id: string;
          name: string;
          storefrontUrl?: string | null;
          storefrontDiscoveryStatus?: BrandOption["storefrontDiscoveryStatus"];
          brandRegistry: boolean;
          brandRegistryAnsweredAt: string | null;
          collaboratorStatus: BrandOption["collaboratorStatus"];
          inviteSentAt: string | null;
          verifiedAt: string | null;
        }[];
      }>("/customer/brands").then((res) => {
        if (!res.data?.brands) return;
        setBrands(
          res.data.brands.map((b) => ({
            id: b.id,
            name: b.name,
            storefrontUrl: b.storefrontUrl ?? null,
            storefrontDiscoveryStatus: b.storefrontDiscoveryStatus,
            brandRegistry: b.brandRegistry,
            brandRegistryAnsweredAt: b.brandRegistryAnsweredAt,
            collaboratorStatus: b.collaboratorStatus,
            inviteSentAt: b.inviteSentAt,
            verifiedAt: b.verifiedAt,
          }))
        );
      });
    }, 5000);
    return () => clearInterval(id);
  }, [brands]);

  function onBrandChange(id: string) {
    setActiveBrandId(id);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ACTIVE_BRAND_KEY, id);
    }
    if (/^\/dashboard\/products\/[^/]+/.test(location.pathname)) {
      navigate("/dashboard/products");
    }
  }

  function onOrgRenamed(name: string) {
    setMe((current) =>
      current
        ? {
            ...current,
            orgs: current.orgs.map((org) =>
              org.id === current.activeOrgId ? { ...org, name } : org
            ),
          }
        : current
    );
  }

  if (meLoading || !me || brands === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400 dark:text-slate-500" />
      </div>
    );
  }

  const isOwner =
    me.orgs.find((o) => o.id === me.activeOrgId)?.role === "owner";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <AppTopBar
        user={{ email: me.user.email, name: me.user.name }}
        orgs={me.orgs}
        activeOrgId={me.activeOrgId}
        brands={brands}
        activeBrandId={activeBrandId}
        onBrandChange={onBrandChange}
        onToggleSidebar={() => setCollapsed((v) => !v)}
        isOwner={isOwner}
      />
      <div className="flex">
        <AppSidebar collapsed={collapsed} isOwner={isOwner} />
        <main className="min-w-0 flex-1">
          {children({
            user: me.user,
            orgs: me.orgs,
            activeOrgId: me.activeOrgId,
            brands,
            activeBrandId,
            onBrandChange,
            onOrgRenamed,
            isOwner,
          })}
        </main>
      </div>
    </div>
  );
}
